import React, { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import VehicleImportCalculator from '../../components/tools/VehicleImportCalculator';
import CalculatorErrorBoundary from '../../components/CalculatorErrorBoundary';
import {
  PUBLIC_CALCULATOR_EMBED_KEY,
  setPublicCalculatorEmbedActive,
  isPublicCalculatorEmbedFromPage,
} from '../../config/embedFlags';

const LOG = '[Bidmanager tools/calculator]';

/** HTML links use &amp; — if that lands in the URL bar, URLSearchParams sees a broken "from" value. */
function normalizeCalculatorSearch(search) {
  if (!search || search.length <= 1) return search;
  if (!search.includes('&amp;') && !search.includes('%26amp%3B')) return search;
  let q = search.slice(1);
  q = q.replace(/%26amp%3B/gi, '&').replace(/&amp;/gi, '&');
  return `?${q}`;
}

/**
 * Public embed: no sidebar, no login gate — extension/modal iframes.
 * Locale: ?lang=hy (or en, ka, ru, …). Query prefill aligned with Autobuy embed docs.
 */
const CalculatorEmbedPage = () => {
  setPublicCalculatorEmbedActive(true);
  if (typeof sessionStorage !== 'undefined') {
    try {
      sessionStorage.setItem(PUBLIC_CALCULATOR_EMBED_KEY, '1');
    } catch {
      /* Embedded webviews can block sessionStorage. */
    }
  }

  const location = useLocation();

  const normalizedSearch = useMemo(
    () => normalizeCalculatorSearch(location.search),
    [location.search]
  );

  const searchParams = useMemo(() => {
    const s = normalizedSearch.startsWith('?') ? normalizedSearch.slice(1) : normalizedSearch;
    return new URLSearchParams(s);
  }, [normalizedSearch]);

  const calcDebug =
    searchParams.get('calcDebug') === '1' ||
    (typeof window !== 'undefined' &&
      (window.__BIDMANAGER_CALC_DEBUG__ === true || window.__AUTOBUY_CALC_DEBUG__ === true));

  useEffect(
    () => () => {
      setPublicCalculatorEmbedActive(false);
      if (typeof sessionStorage !== 'undefined') {
        try {
          sessionStorage.removeItem(PUBLIC_CALCULATOR_EMBED_KEY);
        } catch {
          /* Embedded webviews can block sessionStorage. */
        }
      }
    },
    []
  );

  const calculatorInitialQuery = useMemo(() => {
    const keys = [
      'auction',
      'price',
      'year',
      'month',
      'day',
      'engineVol',
      'engineType',
      'carScale',
      'carType',
      'enableInsurance',
      'isJuridical',
      'country',
      'location',
      'destination',
      'userId',
    ];
    const o = {};
    keys.forEach((k) => {
      const v = searchParams.get(k);
      if (v != null && v !== '') o[k] = v;
    });
    return o;
  }, [searchParams]);

  const fromParam = searchParams.get('from');
  const fromDashboard = fromParam === 'dashboard';
  const embedFromApp = fromParam === 'app' || fromParam === 'extension';

  useEffect(() => {
    const paramsObj = Object.fromEntries(searchParams.entries());
    const base = {
      href: typeof window !== 'undefined' ? window.location.href : '',
      pathname: location.pathname,
      searchRaw: location.search,
      normalizedSearch,
      searchChangedByNormalize: location.search !== normalizedSearch,
      params: paramsObj,
      initialQueryKeys: Object.keys(calculatorInitialQuery),
      fromDashboard,
      embedFlag: isPublicCalculatorEmbedFromPage(),
      viewport:
        typeof window !== 'undefined'
          ? { innerW: window.innerWidth, innerH: window.innerHeight }
          : null,
    };
    if (calcDebug) {
      // eslint-disable-next-line no-console
      console.log(LOG, 'calcDebug', base);
    } else {
      // eslint-disable-next-line no-console
      console.info(LOG, 'embed', {
        pathname: base.pathname,
        queryKeys: Object.keys(paramsObj),
        fromDashboard: base.fromDashboard,
        normalizedAmpersands: base.searchChangedByNormalize,
        initialQueryKeys: base.initialQueryKeys,
      });
    }
  }, [
    location.pathname,
    location.search,
    normalizedSearch,
    searchParams,
    calculatorInitialQuery,
    fromDashboard,
    calcDebug,
  ]);

  return (
    <div
      className="calculator-embed-root"
      style={{ overflow: 'auto', boxSizing: 'border-box', background: '#fff', minHeight: '100vh' }}
    >
      <CalculatorErrorBoundary>
        <VehicleImportCalculator
          fromDashboard={fromDashboard}
          embedFromApp={embedFromApp}
          initialQuery={calculatorInitialQuery}
          compact
        />
      </CalculatorErrorBoundary>
    </div>
  );
};

export default CalculatorEmbedPage;
